/**
 * LiveChatWidget
 *
 * Floating support chat button (bottom-right) that opens a 360×520 slide-up
 * panel with simulated bot responses. Renders globally via main.tsx so it
 * appears on every page.
 *
 * External trigger:
 *   import { openLiveChat } from '@/components/common/LiveChatWidget';
 *   openLiveChat(); // dispatches a custom DOM event the widget listens to
 */

import { useEffect, useRef, useState } from 'react';
import styles from './LiveChatWidget.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  id: string;
  role: 'bot' | 'user';
  text: string;
  timestamp: Date;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

function buildInitialMessages(): ChatMessage[] {
  return [
    {
      id: '1',
      role: 'bot',
      text: '👋 Hi there! Welcome to MangoGridStudio support. How can we help you today?',
      timestamp: new Date(),
    },
  ];
}

function getBotResponse(userMessage: string): string {
  const msg = userMessage.toLowerCase();
  if (msg.includes('trial') || msg.includes('free'))
    return "Our 30-day free trial includes full platform access — no credit card required. Click 'Start Free Trial' on the plan selection page to get started!";
  if (
    msg.includes('price') ||
    msg.includes('cost') ||
    msg.includes('enterprise') ||
    msg.includes('pricing')
  )
    return "Enterprise pricing depends on your system scale and number of users. Send us an email at info@spenergy.solutions and we'll get back to you within 1 business day.";
  if (
    msg.includes('pjm') ||
    msg.includes('miso') ||
    msg.includes('caiso') ||
    msg.includes('iso') ||
    msg.includes('data')
  )
    return 'MangoGridStudio connects to PJM, MISO, and CAISO real-time data feeds. The full trial includes all ISO integrations. Want me to show you how to set it up?';
  if (
    msg.includes('solver') ||
    msg.includes('benders') ||
    msg.includes('admm') ||
    msg.includes('optimization')
  )
    return 'Our solver stack includes HiGHS, Benders decomposition, ADMM zonal coordination, and Lagrangian relaxation. All solvers are available in the trial and enterprise tiers.';
  if (msg.includes('hello') || msg.includes('hi') || msg.includes('hey'))
    return "Hey! Great to hear from you. What can I help you with today — account setup, features, or pricing?";
  if (
    msg.includes('password') ||
    msg.includes('login') ||
    msg.includes('sign in') ||
    msg.includes('account')
  )
    return "For account issues, make sure you're using the correct username or email. If you're still stuck, use 'Forgot password?' on the sign-in page, or email support@mangogridstudio.com.";
  return "Thanks for reaching out! For detailed questions, our team is available at support@mangogridstudio.com. We typically respond within a few hours during business hours (9am–6pm ET).";
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// Module-level event bridge — lets any page open the chat without prop-drilling
// ---------------------------------------------------------------------------

/** Call this from anywhere to programmatically open the live chat panel. */
export function openLiveChat(): void {
  window.dispatchEvent(new CustomEvent('mangogrid:open-chat'));
}

// ---------------------------------------------------------------------------
// SVG icons (inline — no external dependency)
// ---------------------------------------------------------------------------

function ChatBubbleIcon(): JSX.Element {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        fill="currentColor"
      />
    </svg>
  );
}

function SendArrowIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <line x1="22" y1="2" x2="11" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" fill="currentColor" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// LiveChatWidget component
// ---------------------------------------------------------------------------

export default function LiveChatWidget(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(buildInitialMessages);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Listen for external open trigger
  useEffect(() => {
    const handler = (): void => {
      setIsOpen(true);
      setUnreadCount(0);
    };
    window.addEventListener('mangogrid:open-chat', handler);
    return (): void => {
      window.removeEventListener('mangogrid:open-chat', handler);
    };
  }, []);

  // Escape key closes panel
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handler);
    return (): void => {
      window.removeEventListener('keydown', handler);
    };
  }, [isOpen]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (messagesEndRef.current !== null) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current !== null) {
      // Small delay so the slide-up animation starts before focus moves
      const id = window.setTimeout(() => { inputRef.current?.focus(); }, 50);
      return (): void => { window.clearTimeout(id); };
    }
    return undefined;
  }, [isOpen]);

  const handleOpen = (): void => {
    setIsOpen(true);
    setUnreadCount(0);
  };

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const sendMessage = (): void => {
    const trimmed = input.trim();
    if (trimmed.length === 0) return;

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      text: trimmed,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    window.setTimeout(() => {
      const botText = getBotResponse(trimmed);
      const botMsg: ChatMessage = {
        id: `b-${Date.now()}`,
        role: 'bot',
        text: botText,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
      // Increment unread badge if panel is currently closed
      setIsOpen((currentlyOpen) => {
        if (!currentlyOpen) {
          setUnreadCount((n) => n + 1);
        }
        return currentlyOpen;
      });
    }, 1200);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  const isSendDisabled = input.trim().length === 0 || isTyping;

  return (
    <div data-testid="live-chat-widget">
      {/* ----------------------------------------------------------------
          Floating trigger button (always visible when panel is closed)
          ---------------------------------------------------------------- */}
      {!isOpen && (
        <button
          type="button"
          className={styles.triggerButton}
          onClick={handleOpen}
          aria-label="Open live chat support"
        >
          <ChatBubbleIcon />
          <span className={styles.statusDot} aria-hidden="true" />
          {unreadCount > 0 && (
            <span className={styles.unreadBadge} aria-label={`${unreadCount} unread messages`}>
              {unreadCount}
            </span>
          )}
        </button>
      )}

      {/* ----------------------------------------------------------------
          Chat panel (open state)
          ---------------------------------------------------------------- */}
      {isOpen && (
        <div
          className={styles.panel}
          role="dialog"
          aria-label="MangoGrid Support chat"
          aria-modal="false"
        >
          {/* Header */}
          <div className={styles.panelHeader}>
            <div className={styles.headerLeft}>
              <span className={styles.headerStatusDot} aria-hidden="true" />
              <div className={styles.headerInfo}>
                <span className={styles.headerTitle}>MangoGrid Support</span>
                <span className={styles.headerSubtitle}>Typically replies in minutes</span>
              </div>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          {/* Messages area */}
          <div
            className={styles.messagesArea}
            ref={messagesEndRef}
            role="log"
            aria-label="Chat messages"
            aria-live="polite"
          >
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`${styles.messageRow} ${
                  msg.role === 'bot' ? styles.messageRowBot : styles.messageRowUser
                }`}
              >
                <div
                  className={`${styles.bubble} ${
                    msg.role === 'bot' ? styles.bubbleBot : styles.bubbleUser
                  }`}
                >
                  {msg.text}
                </div>
                <span className={styles.timestamp}>{formatTime(msg.timestamp)}</span>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className={`${styles.messageRow} ${styles.messageRowBot}`}>
                <div className={`${styles.bubble} ${styles.bubbleBot} ${styles.typingBubble}`}>
                  <span className={styles.typingDot} aria-hidden="true" />
                  <span className={styles.typingDot} aria-hidden="true" />
                  <span className={styles.typingDot} aria-hidden="true" />
                </div>
              </div>
            )}
          </div>

          {/* Input row */}
          <div className={styles.inputRow}>
            <input
              ref={inputRef}
              type="text"
              className={styles.messageInput}
              placeholder="Type a message..."
              value={input}
              onChange={(e): void => { setInput(e.target.value); }}
              onKeyDown={handleKeyDown}
              aria-label="Type your message"
              disabled={isTyping}
            />
            <button
              type="button"
              className={styles.sendButton}
              onClick={sendMessage}
              disabled={isSendDisabled}
              aria-label="Send message"
            >
              <SendArrowIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
